import {
  keepPreviousData,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMe } from '@/auth/useAuth';
import { ApiError } from '@/lib/api';
import { StudyShell } from './StudyShell';
import {
  getStudyHome,
  getStudyTask,
  listStudyAssessments,
  listStudyTasks,
  STUDY_ASSESSMENTS_QUERY_KEY,
  STUDY_HOME_QUERY_KEY,
  STUDY_TASKS_QUERY_KEY,
  studyTaskQueryKey,
  type StudyHome,
  type StudyTask,
} from './studyApi';
import { DEFAULT_STUDY_TIMEZONE, todayInTimezone, wrapUpWindow } from './studyTermProgress';
import {
  commitStudyRollover,
  getStudyRolloverDraft,
  previewStudyRollover,
  type RolloverDraft,
} from './studyYearApi';
import { STUDY_HOME_PATH, journeyHref } from './studyYearRoutes';
import { LookBackStep, NextTermStep, UnfinishedWorkStep, WrapUpStepBar } from './StudyWrapUpSteps';
import { WrapUpSummary } from './StudyWrapUpSummary';
import { wrapUpMotion } from './studyMotionMoments';
import {
  bringEverything,
  buildRolloverPlan,
  choicesComplete,
  chosenCount,
  clearWrapUpDraft,
  draftChanges,
  initialWrapUpState,
  isYearWrapUp,
  nextPeriodLabel,
  readWrapUpDraft,
  reconcileWrapUpState,
  saveWrapUpDraft,
  WRAP_UP_STEPS,
  wrapUpStorageKey,
  type WrapUpState,
} from './studyWrapUpDraft';

export const ROLLOVER_DRAFT_QUERY_KEY = ['study-rollover-draft'] as const;
/** The draft or Term changed on another device after the learner started (PRD §5.5). */
const CONFLICT_CODES = new Set([
  'STUDY_ROLLOVER_PREVIEW_STALE',
  'STUDY_ROLLOVER_DISPOSITIONS_CHANGED',
]);
/** The Term was already wrapped up elsewhere: there is nothing left to commit. */
const DONE_ELSEWHERE_CODES = new Set([
  'STUDY_TERM_NOT_CURRENT',
  'STUDY_YEAR_NOT_CURRENT',
  'STUDY_NEXT_YEAR_ALREADY_EXISTS',
]);

const errorCode = (error: unknown) => (error instanceof ApiError ? error.code : undefined);

function useOnline() {
  const [online, setOnline] = useState(() =>
    typeof navigator === 'undefined' ? true : navigator.onLine !== false,
  );
  useEffect(() => {
    const update = () => setOnline(navigator.onLine !== false);
    window.addEventListener('online', update);
    window.addEventListener('offline', update);
    return () => {
      window.removeEventListener('online', update);
      window.removeEventListener('offline', update);
    };
  }, []);
  return online;
}

function dateSourceLabel(home: StudyHome | undefined, draft: RolloverDraft, changed: boolean) {
  if (changed) return 'You set these';
  if (isYearWrapUp(draft) || !draft.starts_on) return null;
  const next = home?.terms.find((term) => term.term_number === draft.term_number + 1);
  switch (next?.date_provenance) {
    case 'official':
      return home?.academic_year?.jurisdiction
        ? `From the ${home.academic_year.jurisdiction} school calendar`
        : 'From the school calendar';
    case 'school':
      return 'From your school';
    case 'learner':
      return 'You set these';
    default:
      return null;
  }
}

/** End-of-Term wrap-up: Look back → Unfinished work → Next Term (study-my-year-prd.md §5). */
export function StudyWrapUpPage() {
  const me = useMe();
  const owner = me.data?.kind === 'kid' ? me.data.sub : 'anonymous';
  const home = useQuery({ queryKey: STUDY_HOME_QUERY_KEY, queryFn: getStudyHome, retry: false });
  const draft = useQuery({
    queryKey: ROLLOVER_DRAFT_QUERY_KEY,
    queryFn: getStudyRolloverDraft,
    retry: false,
    refetchOnWindowFocus: false,
  });
  const noTerm =
    errorCode(draft.error) === 'STUDY_TERM_NOT_CURRENT' || home.data?.current_term === null;
  return (
    <StudyShell>
      <div className="mx-auto w-full max-w-2xl">
        {draft.isLoading && <p role="status">Loading your Term…</p>}
        {draft.isError && noTerm && (
          <div className="rounded-3xl bg-surface p-6">
            <p className="font-bold text-ink">Set up this Term first.</p>
            <Link className="btn-pill-primary mt-4 inline-flex" to="/learn/study?setup=term">
              Set up this Term
            </Link>
          </div>
        )}
        {draft.isError && !noTerm && (
          <div role="alert" className="rounded-3xl border border-hairline bg-canvas-pure p-6">
            <p className="text-ink">This part didn&apos;t load. Your work is still saved.</p>
            <button
              type="button"
              className="btn-pill-ghost mt-3"
              onClick={() => void draft.refetch()}
            >
              Try again
            </button>
          </div>
        )}
        {draft.data && (
          <WrapUpFlow
            key={draft.data.from_term_id}
            draft={draft.data}
            home={home.data}
            owner={owner}
            refetchDraft={async () => (await draft.refetch()).data}
          />
        )}
      </div>
    </StudyShell>
  );
}

function WrapUpFlow({
  draft,
  home,
  owner,
  refetchDraft,
}: {
  draft: RolloverDraft;
  home: StudyHome | undefined;
  owner: string;
  refetchDraft: () => Promise<RolloverDraft | undefined>;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const online = useOnline();
  const storageKey = wrapUpStorageKey(owner, draft.from_term_id);
  const [state, setState] = useState<WrapUpState>(() => {
    const saved = readWrapUpDraft(storageKey);
    return saved ? reconcileWrapUpState(saved, draft) : initialWrapUpState(draft);
  });
  const [editingDates, setEditingDates] = useState(false);
  const [datesChanged, setDatesChanged] = useState(false);
  const [changes, setChanges] = useState<{ added: string[]; removed: string[] }>({
    added: [],
    removed: [],
  });
  const [conflict, setConflict] = useState(false);
  const [doneElsewhere, setDoneElsewhere] = useState(false);
  const requestIds = useRef(new Map<string, string>());
  const heading = useRef<HTMLHeadingElement>(null);
  const firstRender = useRef(true);

  const year = isYearWrapUp(draft);
  const next = nextPeriodLabel(draft);
  const from = year ? String(draft.school_year) : `Term ${draft.term_number}`;
  const timezone = home?.academic_year?.timezone ?? DEFAULT_STUDY_TIMEZONE;
  const today = todayInTimezone(timezone);
  const termWindow = home?.current_term ? wrapUpWindow(home.current_term, today) : null;
  const plan = useMemo(() => buildRolloverPlan(draft, state), [draft, state]);
  const fingerprint = JSON.stringify(plan);

  // Subjects for the item cards. The draft has none, so the learner's own lists fill them in.
  const tasks = useQuery({
    queryKey: STUDY_TASKS_QUERY_KEY,
    queryFn: listStudyTasks,
    retry: false,
  });
  const assessments = useQuery({
    queryKey: STUDY_ASSESSMENTS_QUERY_KEY,
    queryFn: listStudyAssessments,
    retry: false,
  });
  // The task list holds only the 100 newest tasks. A wrap-up task outside it is read by id, so
  // its subject shows and the unticked-subject check still covers it.
  const listedIds = new Set((tasks.data ?? []).map((task) => task.id));
  const unlistedIds = tasks.data
    ? draft.tasks.map((task) => task.id).filter((id) => !listedIds.has(id))
    : [];
  const unlisted = useQueries({
    queries: unlistedIds.map((id) => ({
      queryKey: studyTaskQueryKey(id),
      queryFn: () => getStudyTask(id),
      retry: false,
    })),
  });
  const unlistedTasks = unlisted
    .map((query) => query.data)
    .filter((task): task is StudyTask => Boolean(task));
  const unlistedKey = unlistedTasks.map((task) => task.id).join(',');
  const lookupPending = tasks.isLoading || unlisted.some((query) => query.isLoading);
  const lookup = useMemo(() => {
    const map = new Map<string, { subject: string | null; key: string | null }>();
    for (const task of [...(tasks.data ?? []), ...unlistedTasks])
      map.set(task.id, {
        subject: task.subject?.display_name ?? null,
        key: task.subject?.subject_key ?? null,
      });
    for (const assessment of assessments.data ?? [])
      map.set(assessment.id, {
        subject: assessment.subject.display_name,
        key: assessment.subject.subject_key,
      });
    return map;
    // `unlistedTasks` is rebuilt each render; its ids are the dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tasks.data, assessments.data, unlistedKey]);

  /** A kept item whose subject is unticked would be refused by the server. */
  const missingSubject = useMemo(() => {
    const kept = [...plan.tasks, ...plan.assessments].filter((item) => item.action !== 'archive');
    const keys = kept
      .map((item) => lookup.get(item.id)?.key)
      .filter((key): key is string => Boolean(key) && !plan.subject_keys.includes(key as string));
    const subject = draft.subjects.find((row) => keys.includes(row.subject_key));
    return subject?.display_name ?? null;
  }, [draft.subjects, lookup, plan]);
  const datesInvalid =
    Boolean(state.startsOn) !== Boolean(state.endsOn) ||
    (Boolean(state.startsOn) && state.endsOn < state.startsOn);

  const preview = useQuery({
    queryKey: ['study-rollover-preview', fingerprint],
    queryFn: () => previewStudyRollover(plan),
    enabled: state.step === 3 && online && !missingSubject && !datesInvalid && !doneElsewhere,
    placeholderData: keepPreviousData,
    retry: false,
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
  });
  const summaryCurrent =
    preview.data !== undefined && !preview.isPlaceholderData && !preview.isFetching;

  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    heading.current?.focus();
  }, [state.step]);

  const goToConflict = async () => {
    const latest = await refetchDraft();
    if (latest) {
      setChanges(draftChanges(draft, latest));
      setState((current) => ({ ...reconcileWrapUpState(current, latest), step: 2 }));
    } else setState((current) => ({ ...current, step: 2 }));
    setConflict(true);
  };

  const previewCode = errorCode(preview.error);
  useEffect(() => {
    if (!previewCode) return;
    if (DONE_ELSEWHERE_CODES.has(previewCode)) setDoneElsewhere(true);
    else if (CONFLICT_CODES.has(previewCode)) void goToConflict();
    // goToConflict reads the latest props itself; the code is the only trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewCode]);

  const commit = useMutation({
    mutationFn: () => {
      if (!preview.data) throw new Error('Preview missing');
      const key = `${fingerprint}:${preview.data.snapshot}`;
      let requestId = requestIds.current.get(key);
      if (!requestId) {
        requestId = crypto.randomUUID();
        requestIds.current.set(key, requestId);
      }
      return commitStudyRollover({
        plan,
        expected_snapshot: preview.data.snapshot,
        request_id: requestId,
      }).catch((error: unknown) => {
        // Invariants fail loud: the ids let support find the attempt; no titles or text.
        console.error('[study-wrap-up] commit failed', {
          kid_id: owner,
          term_id: draft.from_term_id,
          request_id: requestId,
          code: errorCode(error) ?? 'UNKNOWN',
          server_request_id: error instanceof ApiError ? error.requestId : undefined,
        });
        throw error;
      });
    },
    onSuccess: async () => {
      clearWrapUpDraft(storageKey);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: STUDY_HOME_QUERY_KEY }),
        queryClient.invalidateQueries({ queryKey: ROLLOVER_DRAFT_QUERY_KEY }),
        queryClient.invalidateQueries({ queryKey: STUDY_TASKS_QUERY_KEY }),
        queryClient.invalidateQueries({ queryKey: STUDY_ASSESSMENTS_QUERY_KEY }),
        queryClient.invalidateQueries({ queryKey: ['study-journey'] }),
      ]);
      await wrapUpMotion();
      navigate(STUDY_HOME_PATH, { state: { notice: `${next} is ready.` } });
    },
    onError: (error) => {
      const code = errorCode(error);
      if (code && DONE_ELSEWHERE_CODES.has(code)) setDoneElsewhere(true);
      else if (code && CONFLICT_CODES.has(code)) void goToConflict();
    },
  });

  useEffect(() => {
    if (doneElsewhere) clearWrapUpDraft(storageKey);
  }, [doneElsewhere, storageKey]);

  // A failed Start belongs to the choices it sent. When the choices change, its error box goes,
  // so the only way on is the main button, after the new summary is shown.
  const resetCommit = commit.reset;
  useEffect(() => {
    resetCommit();
  }, [fingerprint, resetCommit]);

  const update = (patch: Partial<WrapUpState>) => setState((current) => ({ ...current, ...patch }));
  const saveLocally = () => saveWrapUpDraft(storageKey, state);

  if (doneElsewhere)
    return (
      <div className="rounded-3xl bg-surface p-6">
        <p className="font-bold text-ink">{from} was already wrapped up on another device.</p>
        <Link className="btn-pill-primary mt-4 inline-flex" to={STUDY_HOME_PATH}>
          Back to Home
        </Link>
      </div>
    );

  const stepLabel = WRAP_UP_STEPS[state.step - 1].label;
  const complete = choicesComplete(draft, state.choices);
  const commitCode = errorCode(commit.error);
  const handled = (code: string | undefined) =>
    Boolean(code && (CONFLICT_CODES.has(code) || DONE_ELSEWHERE_CODES.has(code)));
  const commitFailed = commit.isError && !handled(commitCode);
  const previewFailed = preview.isError && !handled(previewCode);
  const canStart =
    online &&
    summaryCurrent &&
    !commit.isPending &&
    !lookupPending &&
    !missingSubject &&
    !datesInvalid;

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-3xl font-extrabold text-ink sm:text-4xl">Wrap up {from}</h1>
        <div className="mt-4">
          <WrapUpStepBar step={state.step} />
        </div>
      </header>
      {state.step === 1 && termWindow?.kind === 'before_window' && (
        <p className="rounded-2xl bg-wash-sunshine p-4 text-sm font-bold text-ink">
          {home?.current_term ? `Term ${home.current_term.term_number}` : from} still has{' '}
          {termWindow.weeksLeft} {termWindow.weeksLeft === 1 ? 'week' : 'weeks'} left. Wrapping up
          now starts {next} today.
        </p>
      )}
      {conflict && state.step === 2 && (
        <p role="alert" className="rounded-2xl bg-wash-sunshine p-4 text-sm font-bold text-ink">
          Something changed since you started (for example, a task was finished on another device).
          Check step 2 again.
        </p>
      )}
      <section
        aria-labelledby="wrap-up-step-heading"
        className="rounded-3xl border border-hairline bg-canvas-pure p-5 sm:p-6"
      >
        <h2
          id="wrap-up-step-heading"
          ref={heading}
          tabIndex={-1}
          className="mb-4 text-2xl font-extrabold text-ink outline-none"
        >
          {state.step === 1
            ? `Look back on ${from}`
            : state.step === 3 && year
              ? 'Next year'
              : stepLabel}
        </h2>
        {state.step === 1 && (
          <LookBackStep
            draft={draft}
            reflection={state.reflection}
            onReflection={(reflection) => update({ reflection })}
            storyHref={journeyHref({ year: draft.academic_year_id, returnTo: 'wrap-up' }, '#story')}
            onOpenStory={saveLocally}
          />
        )}
        {state.step === 2 && (
          <UnfinishedWorkStep
            draft={draft}
            state={state}
            today={today}
            lookup={lookup}
            added={changes.added}
            removed={changes.removed}
            chosen={chosenCount(draft, state.choices)}
            onBringEverything={() => update({ choices: bringEverything(draft) })}
            onTask={(id, choice) =>
              setState((current) => ({
                ...current,
                choices: {
                  ...current.choices,
                  tasks: { ...current.choices.tasks, [id]: choice },
                },
              }))
            }
            onAssessment={(id, choice) =>
              setState((current) => ({
                ...current,
                choices: {
                  ...current.choices,
                  assessments: { ...current.choices.assessments, [id]: choice },
                },
              }))
            }
          />
        )}
        {state.step === 3 && (
          <>
            <NextTermStep
              draft={draft}
              state={state}
              dateSource={dateSourceLabel(home, draft, datesChanged)}
              editingDates={editingDates}
              onEditDates={() => setEditingDates(true)}
              onChange={(patch) => {
                if (patch.startsOn !== undefined || patch.endsOn !== undefined)
                  setDatesChanged(true);
                update(patch);
              }}
            />
            <WrapUpSummary
              plan={plan}
              preview={preview.data}
              current={summaryCurrent}
              from={from}
              missingSubject={missingSubject}
              datesInvalid={datesInvalid}
              previewFailed={previewFailed}
              onRetryPreview={() => void preview.refetch()}
            />
          </>
        )}
      </section>
      {commitFailed && (
        <div role="alert" className="rounded-2xl bg-wash-coral p-4 text-sm text-ink">
          <p className="font-bold">We couldn&apos;t start {next}. Your choices are still here.</p>
          <button
            type="button"
            className="btn-pill-ghost mt-3"
            disabled={!canStart}
            onClick={() => commit.mutate()}
          >
            Try again
          </button>
        </div>
      )}
      {!online && state.step === 3 && (
        <p role="status" className="text-sm font-bold text-ink">
          You&apos;re offline.
        </p>
      )}
      <nav aria-label="Wrap-up actions" className="flex flex-wrap items-center gap-3">
        {state.step > 1 && (
          <button
            type="button"
            className="btn-pill-ghost"
            onClick={() => update({ step: (state.step - 1) as WrapUpState['step'] })}
          >
            Back
          </button>
        )}
        {state.step < 3 && (
          <button
            type="button"
            className="btn-pill-primary"
            disabled={state.step === 2 && !complete}
            onClick={() => {
              if (state.step === 2) setConflict(false);
              update({ step: (state.step + 1) as WrapUpState['step'] });
            }}
          >
            Next
          </button>
        )}
        {state.step === 3 && (
          <button
            type="button"
            className="btn-pill-primary"
            disabled={!canStart}
            onClick={() => commit.mutate()}
          >
            {commit.isPending ? 'Starting…' : preview.isFetching ? 'Updating…' : `Start ${next}`}
          </button>
        )}
        <button
          type="button"
          className="ml-auto text-sm font-extrabold text-slate2 underline underline-offset-4 hover:text-ink"
          onClick={() => {
            saveLocally();
            navigate(STUDY_HOME_PATH, {
              state: { notice: 'Your wrap-up choices are saved on this device.' },
            });
          }}
        >
          Save and leave
        </button>
      </nav>
    </div>
  );
}
